/**
 * Created by Amit Panghal on 9/03/2018.
 */
import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {GameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/GameConfig";
import {ReelSymbolName} from "@nolimitcity/slot-game/bin/core/reelsymbol/ReelSymbolName";
import {ISymbolStateConfig, StateReelSymbol} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";
import {IBetLineWinData} from "@nolimitcity/slot-game/bin/core/server/data/IServerData";
import {ServerEvent} from "@nolimitcity/slot-game/bin/core/server/event/ServerEvent";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {ScalableLayer} from "@nolimitcity/slot-game/bin/core/stage/ScalableLayer";
import {StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {Helper} from "@nolimitcity/slot-game/bin/core/utils/Helper";
import {WinPresentationEvent} from "@nolimitcity/slot-game/bin/core/winpresentation/event/WinPresentationEvent";
import {WinRatioType} from "@nolimitcity/slot-game/bin/core/winpresentation/WinRatio";
import {SequentiallyBetLineInitialWPController} from "@nolimitcity/slot-game/bin/game/winpresentation/initial/sequentially/SequentiallyBetLineInitialWPController";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";
import {Timeline, TimelineLite, TweenLite} from "gsap";
import {TractorBeamHelper} from "../../utils/TractorBeamHelper";
import {TractorBeamStateMatrix} from "../../utils/TractorBeamStateMatrix";
import {TractorBeamBackgroundEvent} from "../background/TractorBeamBackground";
import {TractorBeamFreeSpinEvent} from "../freespin/TractorBeamAddFreespinController";
import {TractorBeamStickySymbol} from "../randomfeature/TractorBeamStickySymbol";
import {TractorBeamParsedGameData} from "../server/data/TractorBeamParsedGameData";
import {TractorBeamParsedInitData} from "../server/data/TractorBeamParsedInitData";
import {BasicStateReelSymbol} from "../symbols/BasicStateReelSymbol";
import {TractorBeamGameConfig} from "../TractorBeamGameConfig";
import {TractorBeamGameModuleConfig} from "../TractorBeamGameModuleConfig";
import {TractorBeamSoundConfig} from "../TractorBeamSoundConfig";
import {TractorBetLineEvent} from "./AnimatedMeshRope";
import {TractorBeamInitialWinCountUp} from "./TractorBeamInitialWinCountUp";
import {TractorBeam} from "../TractorBeam";

export interface ITractorBeamBetLineInitialData extends IBetLineWinData {
    lineStart:string;
}

export class TractorBeamInitialWinPresentationController extends SequentiallyBetLineInitialWPController {
    private readonly _tractorBeamIndexes:number[];
    private _winCountUp:TractorBeamInitialWinCountUp;
    private _bgLayer:ScalableLayer;
    private _symbolStates:TractorBeamStateMatrix<number>;
    protected _noWinSymbols:any;
    private _isAborted:boolean = false;
    protected _winSymbolsArray:any;
    private _beamOutLayer:ScalableLayer;
    protected _animationTimeLine:TimelineLite | null;
    private _symbolsToBeamOut:TractorBeamStickySymbol[];
    private _removedSymbols:string[];
    private _commandCenterSymbols:string[];

    private _completed:boolean = false;
    private _playedOnce = false;
    private _extraFreespinAwarded:boolean = false;
    private _fullWildsOnRestore:boolean = false;
    private _isBigWin:boolean = false;

    constructor(indexes:[number, number]) {
        super(indexes);
        this._tractorBeamIndexes = indexes;
        this._beamOutLayer = StageManager.getLayer(TractorBeamGameConfig.instance.LAYERS.SYMBOLS.name);
        this._bgLayer = StageManager.getLayer(TractorBeamGameConfig.instance.LAYERS.BACKGROUND.name);
        this._winCountUp = new TractorBeamInitialWinCountUp();
        this._symbolsToBeamOut = [];
        this._commandCenterSymbols = [];
        this._removedSymbols = [];
        this._winCountUp.position.set(GameConfig.instance.GAME_WIDTH * 0.5, GameConfig.instance.GAME_HEIGHT * 0.5);
        StageManager.getLayer(TractorBeamGameConfig.instance.LAYERS.WIN_COUNT_UP.name).addChild(this._winCountUp);
    }

    protected parseFeatureGameData(data:TractorBeamParsedGameData):void {
        this._extraFreespinAwarded = data.numberOfExtraFreespinsAwarded > 0;
        this._completed = false;
        this._removedSymbols = data.removedSymbols;
        this._winSymbols = [];
        // #226
        TractorBeamGameConfig.instance.WIN_BELOW_STAKE = (TractorBeam.api.gameClientConfiguration.belowStakeWinRestriction && (data.singleWin <= data.playedBetValue));
    }

    private parseFeatureInitData(data:TractorBeamParsedInitData):void {
        this._removedSymbols = data.removedSymbols;
        if(data.mode === "FREESPIN" && data.nextMode !== "NORMAL") {
            TractorBeamGameModuleConfig.tractorBeamCommandCenterView.setInitialRemovedSymbols(this._removedSymbols);
        }
    }

    protected addFeatureEventHandlers():void {
        EventHandler.addEventListener(this, ServerEvent.INIT_DATA_PARSED, (event:GameEvent) => this.parseFeatureInitData(event.params[0]));
        EventHandler.addEventListener(this, TractorBeamBackgroundEvent.TOGGLE_COMMAND_CENTER, (event:GameEvent) => this.resetCommandCenter(event.params[0]));
        EventHandler.addEventListener(this, TractorBeamFreeSpinEvent.ADD_SYMBOL_TO_CC, (event:GameEvent) => this.addRemovedSymbolToCommandCenter(event.params[0]));
    }


    protected addWinCountUpToTimeline(timeline:Timeline):void {
        if(this._gameData!.isBigWin) {
            return;
        }

        const totalWin:number = this._gameData!.totalWin;
        const from:number = totalWin - this._gameData!.singleWin;
        const duration:number = this._gameData!.currentWinRatioConfig!.duration;
        const type:WinRatioType | undefined = this._gameData!.currentWinRatioConfig!.type;

        const countUpTimeline:TimelineLite = new TimelineLite();
        countUpTimeline.add([
            (type === WinRatioType.MEDIUM || type === WinRatioType.LARGE) ? () => TractorBeamGameModuleConfig.soundPlayer.startCountUp() : () => {},
            this._winCountUp.getCountUpAnimation(0, this._gameData!.singleWin, duration, this._gameData!.currentMultiplier, (type === WinRatioType.TINY || type === WinRatioType.SMALL))
        ]);
        countUpTimeline.add([
            () => SlotGame.winFieldController.showWinField(totalWin),
            (type === WinRatioType.MEDIUM || type === WinRatioType.LARGE) ? () => TractorBeamGameModuleConfig.soundPlayer.stopCountUp(true) : () => TractorBeamGameModuleConfig.soundPlayer.stopCountUp(false)
        ]);
        //countUpTimeline.add(() => {}, countUpTimeline.totalDuration() + 0.4);

        timeline.add(countUpTimeline, 0);
    }

    protected finish(isAborted:boolean = false):void {
        this._animationTimeLine = new TimelineLite();
        Logger.logDev(`[WinPresentation]-${this.moduleName}.finish()`);
        this._isPlaying = false;
        this.stopWinPresentation(isAborted);
        if(!(this._gameData!.mode === "FREESPIN" && this._gameData!.nextMode === "NORMAL")) {
            if(!this._completed) {
                EventHandler.dispatchEvent(new GameEvent(WinPresentationEvent.WIN_PRESENTATION_COMPLETED, this._tractorBeamIndexes));
                this._completed = true;
            }
        }
    }

    protected stopWinPresentation(isAborted:boolean):void {
        this._bgLayer.interactive = false;
        if(this._timeline) {
            TractorBeamGameModuleConfig.soundPlayer.stopCountUp(false);
            if(this._timeline.isActive()) {
                this._timeline.pause();
                this._timeline.kill();
            }
            this._timeline = null;
        }
        if(!this._gameData) {
            return;
        }
        for(let i = 0; i < this._symbolsToBeamOut.length; i++) {
            this._symbolsToBeamOut[i].alpha = 0;
            this._beamOutLayer.removeChild(this._symbolsToBeamOut[i]);
        }
        if(this._gameData.mode === "FREESPIN" && this._gameData.nextMode !== "NORMAL") {
            this.handleFreeSpinStopPresentation();
        }

        if((this._gameData!.mode === "FREESPIN" && this._gameData!.nextMode === "NORMAL")) {
            let stopTimeLine:TimelineLite = new TimelineLite({
                onComplete : () => {
                    if(!this._completed) {
                        EventHandler.dispatchEvent(new GameEvent(WinPresentationEvent.WIN_PRESENTATION_COMPLETED, this._tractorBeamIndexes));
                    }
                    stopTimeLine.pause();
                    stopTimeLine.kill();
                }
            });
            stopTimeLine.add(this._winCountUp.abort(this._gameData.singleWin, (this._isAborted || isAborted), this._gameData.currentMultiplier, this._gameData.isBigWin));
        } else {
            this._winCountUp.abort(this._gameData.singleWin, (this._isAborted || isAborted), this._gameData.currentMultiplier, this._gameData.isBigWin);
        }
        SlotGame.winFieldController.showWinField();
    }

    private handleFreeSpinStopPresentation():void {
        for(let i:number = 0; i < this._winSymbolsArray.length; i++) {
            if(this._winSymbolsArray[i].symbol.symName !== ReelSymbolName.WILD && this._winSymbolsArray[i].symbol.symName !== ReelSymbolName.SCATTER) {
                this._winSymbolsArray[i].symbol.setIsRemovedFromReel = true;
                this._winSymbolsArray[i].symbol.visible = false;
            }
        }
        let removedSymbols = this._removedSymbols;
        let noWinBeamOutSymbols = this._noWinSymbols.filter(function(symItem:any) {
            return removedSymbols.indexOf(symItem.symbol.symName) >= 0;
        });
        for(let i:number = 0; i < noWinBeamOutSymbols.length; i++) {
            if(noWinBeamOutSymbols[i].symbol.symName !== ReelSymbolName.WILD && noWinBeamOutSymbols[i].symbol.symName !== ReelSymbolName.SCATTER) {
                noWinBeamOutSymbols[i].symbol.setIsRemovedFromReel = true;
                noWinBeamOutSymbols[i].symbol.visible = false;
            }
        }
        this.addRemovedSymbolToCommandCenter(false);
    }

    protected playWinSound(type:WinRatioType | undefined):void {
        switch(type) {
            case WinRatioType.TINY:
                // #226
                !TractorBeamGameConfig.instance.WIN_BELOW_STAKE && SlotGame.sound.playEffect(TractorBeamSoundConfig.instance.WIN_TINY);
                break;
            case WinRatioType.SMALL:
                SlotGame.sound.playEffect(TractorBeamSoundConfig.instance.WIN_SMALL);
                break;
            case WinRatioType.MEDIUM:
                SlotGame.sound.playEffect(TractorBeamSoundConfig.instance.WIN_MEDIUM);
                break;
            case WinRatioType.LARGE:
                SlotGame.sound.playEffect(TractorBeamSoundConfig.instance.WIN_LARGE);
                break;
        }
    }

    protected startWinPresentation():void {
        this._symbolsToBeamOut = [];
        this._isAborted = false;
        this._bgLayer.interactive = false;


        this.playWinSound(this._gameData!.currentWinRatioConfig!.type);

        this._bgLayer.on("pointerdown", (data:PIXI.InteractionData) => {
            this._isAborted = true;
            this.finish();
        });
        this._symbolStates = TractorBeamHelper.createSymbolStateMatrixFromBetWinsWithWeights(<ITractorBeamBetLineInitialData[]>this._betWinsData!);
        this._noWinSymbols = TractorBeamHelper.getSymbolsWithState(this._symbolStates, 0);
        this._winSymbolsArray = TractorBeamHelper.getSymbolsWithState(this._symbolStates, 1);
        const timeline:TimelineLite = new TimelineLite({
            paused : true,
            onComplete : () => {
                let delayTimeLineLite:TimelineLite = new TimelineLite({onComplete : () => delayTimeLineLite.kill()});
                delayTimeLineLite.add(TweenLite.delayedCall((this._gameData!.nextMode === "NORMAL" ? 0.3 : 0), this.finish, [], this));
            }
        });

        if(this.playAllNoWinSymbols) {
            this.playAllNoWinSymbols(timeline);
        }
        this.addWinAnimationsToTimeline(timeline);
        if(this._gameData!.mode === "FREESPIN" && this._gameData!.nextMode !== "NORMAL") {


            //if() {
            timeline.add(() => {
                this.playFSSymbolWinAnimation(timeline);
            }, timeline.totalDuration() + 1);
            //} else {
            //   this.playFSSymbolWinAnimation(timeline);
            //}

        }

        if(this._config.hasWinCountUp && this.addWinCountUpToTimeline) {
            this.addWinCountUpToTimeline(timeline);
        }

        timeline.add(() => {}, timeline.totalDuration() + 0.6);

        this._timeline = timeline;
        this._timeline.play();
    }

    protected clearRandomFeature(randomSymbolAnimTimeLine:TimelineLite):void {
        randomSymbolAnimTimeLine.pause();
        randomSymbolAnimTimeLine.kill();
    }

    public getSymbolStateAnimations(symbols:any, newState:ISymbolStateConfig, fadeDuration:number = 0.001):Timeline[] {
        const animations:Timeline[] = [];

        for(let i:number = 0; i < symbols.length; i++) {

            if(!(this._gameData!.mode === "FREESPIN" && symbols[i].symbol.symName === "S")) {
                let animation:Timeline | null = this.getSymbolStateAnimation(symbols[i].symbol, newState, fadeDuration);

                if(animation != null) {
                    animations.push(animation);
                }
            }
        }

        return animations;
    }

    public getSymbolStateAnimation(symbol:StateReelSymbol, newState:ISymbolStateConfig, fadeDuration:number = 0.001):Timeline | null {
        const animation:Timeline | null = symbol.changeState({
            state : newState,
            fadeInDuration : fadeDuration
        });

        return animation;
    }

    protected playAllNoWinSymbols(timeline:TimelineLite) {
        let noWinSymbolsAnimation:Timeline[] = this.getSymbolStateAnimations(this._noWinSymbols, TractorBeamGameConfig.instance.SYMBOL_STATES.noWin, 0.5);
        timeline.add(noWinSymbolsAnimation, 0);
    }

    protected addSingleBetWinAnimationToTimeline(betLineWinData:ITractorBeamBetLineInitialData, timeline:TimelineLite, startTime:number, isInitial:boolean = false):number {
        const symbolsNum:number = betLineWinData.betLineIndex >= 0 ? betLineWinData.nrOfSymbols : (betLineWinData.betLine.length);
        let symbol:StateReelSymbol | null;
        let betLinesPerWinDirection:number = betLineWinData.lineStart === "Right" ? 4 : 0;
        let symbolDelay:number = 0;

        for(let i:number = 0; i < symbolsNum; i++) {
            let reelIndex = Math.abs(betLinesPerWinDirection - i);
            if(betLineWinData.betLine[reelIndex] >= 0) {
                symbol = this.findSymbol(reelIndex, betLineWinData.betLine[reelIndex]);
                if(!symbol) {
                    throw new Error(`Error: SequentiallyBetLineInitialWPController.playBetWinPresentation(): Cannot find symbol at reel ${reelIndex} symbol ${betLineWinData.betLine[reelIndex]}`);
                }

                if(this._winSymbols.indexOf(symbol) === -1) {
                    this._winSymbols.push(symbol);
                }
            }
        }

        if(this._betLineWinConfig.shouldPlayBetLine) {
            timeline.add([
                () => this.playBetLine!(betLineWinData.betLineIndex),
                () => {
                    if(this._winSymbols && this._betLineWinConfig && this._betLineWinConfig.shouldPlayBetLine) {
                        this._winSymbols.forEach((symbol) => {
                            if(symbol) {
                                symbol.changeState({
                                    state : GameConfig.instance.SYMBOL_STATES.win,
                                    setStack : true,
                                    fadeInDuration : 0//0.1
                                })
                            }
                        });
                    }
                }
            ], startTime);
        }

        return this.getNextBetWinAnimationTimeOffset(symbolDelay);
    }

    protected playFSSymbolWinAnimation(timeLine:TimelineLite):void {
        if(timeLine && !this._fullWildsOnRestore) {
            let removedSymbols = this._removedSymbols;
            let noWinBeamOutSymbols = this._noWinSymbols.filter(function(symItem:any) {
                return removedSymbols.indexOf(symItem.symbol.symName) >= 0;
            });
            let tryTimeLine = new TimelineLite();
            tryTimeLine.addLabel('targetPointZero', 0);
            //tryTimeLine.addLabel('targetPointBetline', 0.8);
            tryTimeLine.addLabel('targetPointOne', 0.1);
            tryTimeLine.addLabel('targetPointTwo', 0.36);
            tryTimeLine.delay(0.3);

            let normalSymbolPresent:boolean = false;

            for(let i:number = 0; i < this._winSymbolsArray.length; i++) {
                let winningSymbol = this._winSymbolsArray[i];
                if(this._winSymbolsArray[i].symbol.symName !== ReelSymbolName.SCATTER) {
                    winningSymbol.symbol.setIsRemovedFromReel = true;
                    //winningSymbol.symbol.hideSpineLayer();

                    if(winningSymbol.symbol.symName !== ReelSymbolName.WILD && ["M1", "M2", "M3", "M4", "L1", "L2", "L3", "L4", "W", "S"].indexOf(winningSymbol.symbol.symName) < 0) {
                        let overlaySymbol:TractorBeamStickySymbol = this.createRandomSymbol(winningSymbol);

                        tryTimeLine.to(overlaySymbol.position, 0.26, {y : overlaySymbol.position.y - 100}, "targetPointOne");
                        tryTimeLine.to(overlaySymbol.position, 0.13, {y : -200}, "targetPointTwo");
                    } else {
                        // Hiding symbol on reel (ie. beam out)
                        if(!normalSymbolPresent) {
                            normalSymbolPresent = (this._winSymbolsArray[i].symbol.symName !== "S" && this._winSymbolsArray[i].symbol.symName !== "W");
                        }

                        tryTimeLine.call(() => {
                            const rand:number = Math.random() * 0.5;

                            if(i === 0) {
                                //EventHandler.dispatchEvent(new GameEvent(TractorBetLineEvent.ABORT_ALL_BET_LINES));
                                //new TimelineLite().add(() => {
                                //});
                                if(winningSymbol.symbol.symName !== ReelSymbolName.WILD) {
                                    winningSymbol.symbol.hideSpineLayer();
                                }

                                if(this._removedSymbols.length < 9 && !TractorBeamGameConfig.instance.WIN_BELOW_STAKE) { // #226
                                    SlotGame.sound.playEffect(TractorBeamSoundConfig.instance.BEAM_UP);
                                }
                            } else if(winningSymbol.symbol.symName !== ReelSymbolName.WILD) {
                                new TimelineLite().add(() => winningSymbol.symbol.hideSpineLayer(), rand);
                            }

                            if(i === this._winSymbolsArray.length - 1 && normalSymbolPresent && !TractorBeamGameConfig.instance.WIN_BELOW_STAKE) { // #226
                                SlotGame.sound.playEffect(TractorBeamSoundConfig.instance.BEAM_UP);
                            }

                        }, [], this, "targetPointOne");
                    }
                }
            }
            for(let i = 0; i < noWinBeamOutSymbols.length; i++) {
                if(timeLine && noWinBeamOutSymbols[i].symbol.symName !== ReelSymbolName.SCATTER) {
                    let noWinoverlaySymbol:TractorBeamStickySymbol = this.createRandomSymbol(noWinBeamOutSymbols[i]);

                    if(["M1", "M2", "M3", "M4", "L1", "L2", "L3", "L4", "W", "S"].indexOf(noWinBeamOutSymbols[i].symbol.symName) < 0) {
                        tryTimeLine.call(() => this.hideBetLines(noWinBeamOutSymbols[i].symbol, noWinoverlaySymbol), [], this, "targetPointZero");
                        tryTimeLine.to(noWinoverlaySymbol.position, 0.26, {y : noWinoverlaySymbol.position.y - 100}, "targetPointOne");
                        tryTimeLine.to(noWinoverlaySymbol.position, 0.13, {y : -200}, "targetPointTwo");
                    } else {
                        // Hiding symbol on reel (ie. beam out)
                        tryTimeLine.call(() => {
                            const rand:number = Math.random() * 0.5;

                            new TimelineLite().add(() => noWinBeamOutSymbols[i].symbol.hideSpineLayer(), rand);
                        }, [], this, "targetPointOne");
                    }

                }
            }
            tryTimeLine.call(() => {
                EventHandler.dispatchEvent(new GameEvent(TractorBetLineEvent.ABORT_ALL_BET_LINES, true));
            }, [], this, "targetPointOne"); // TODO: Hide betline stuff
            timeLine.add(tryTimeLine);
            timeLine.add(() => {}, timeLine.totalDuration() + 1.4); // tmp
            //timeLine.timeScale(0.25); // TODO: tmp slow down
        }
    }

    private addRemovedSymbolToCommandCenter(isScatterSymbol:boolean):void {
        let diffArray:string[] = TractorBeamHelper.differenceBetweenArrays(this._removedSymbols, this._commandCenterSymbols);
        diffArray = isScatterSymbol ? diffArray : diffArray.filter(function(item:string) {return item !== ReelSymbolName.SCATTER});
        for(let i = 0; i < diffArray.length; i++) {
            TractorBeamGameModuleConfig.tractorBeamCommandCenterView.tintRemovedSymbol(diffArray[i]);
            this._commandCenterSymbols.push(diffArray[i]);
        }

    }

    private resetCommandCenter(visibility:boolean):void {
        TractorBeamGameModuleConfig.tractorBeamCommandCenterView.resetCommandCenter(visibility);
        this._commandCenterSymbols = [];
        this._playedOnce = false;
        this._fullWildsOnRestore = false;
    }

    private hideBetLines(winningSymbol:BasicStateReelSymbol, overlaySymbol:TractorBeamStickySymbol):void {
        overlaySymbol.alpha = 1;
        winningSymbol.currentStateAnimation!.alpha = 0;
        winningSymbol.currentStateAnimation!.tint = 0xFFFFFF;
        EventHandler.dispatchEvent(new GameEvent(TractorBetLineEvent.ABORT_ALL_BET_LINES));
    }

    private createRandomSymbol(symbol:any):TractorBeamStickySymbol {
        let randomSymbol:TractorBeamStickySymbol;
        randomSymbol = new TractorBeamStickySymbol(symbol.symbol.symName);
        randomSymbol.position.x = Helper.getSymbolPositions(symbol.reelIndex, symbol.symbolIndex)[0];
        randomSymbol.position.y = Helper.getSymbolPositions(symbol.reelIndex, symbol.symbolIndex)[1];
        randomSymbol.alpha = 0;
        this._beamOutLayer.addChild(randomSymbol);
        this._symbolsToBeamOut.push(randomSymbol);
        return randomSymbol;
    }
}
